import { useMemo, useState } from "react"

import useLocalStorage from "@hooks/useLocalStorage"
import useSlugExtractor from "@hooks/useSlugExtractor"

import extractURLSearchParam from "@utils/extractURLSearchParam"

import { UseObjectsViewStateProps } from "@organisms/ObjectsView/ObjectsView.types"
import { ObjectsViewTabProps } from "@organisms/ObjectsView/ObjectsViewControls/ObjectsViewTab/ObjectsViewTab.types"

import {
    LIST_SEARCH_PARAM_ACTIVE_TAB,
    LIST_SEARCH_PARAM_KEYWORD_SEARCH,
    LIST_SEARCH_PARAM_PHASE,
} from "@constants/searchParams"

const phaseOverride = extractURLSearchParam(LIST_SEARCH_PARAM_PHASE) as ObjectPhase
const activeTabOverride = extractURLSearchParam(LIST_SEARCH_PARAM_ACTIVE_TAB)
const searchKeywordsOverride = extractURLSearchParam(LIST_SEARCH_PARAM_KEYWORD_SEARCH)

export default function useObjectsViewState(props: UseObjectsViewStateProps) {
    const {
        activePhaseStorageKey,
        activeTabStorageKey,
        searchKeywordsStorageKey,
        defaultActivePhase,
        defaultFilters,
    } = props

    const [activePhase, setActivePhase] = useLocalStorage<ObjectPhase>(
        activePhaseStorageKey,
        defaultActivePhase,
        phaseOverride,
    )
    const [savedActiveTab, setActiveTab] = useLocalStorage<ObjectsViewTabProps | undefined>(
        activeTabStorageKey,
        undefined,
    )
    // The tab is saved with its route templates and gets the current company's URLs here. Old saves held the bare
    // title, which the tabs still read as it is.
    const [listEndpoint, countEndpoint] = useSlugExtractor([
        savedActiveTab?.listEndpoint ?? "",
        savedActiveTab?.countEndpoint ?? "",
    ])
    const activeTab = useMemo(
        () =>
            typeof savedActiveTab === "object" ? { ...savedActiveTab, listEndpoint, countEndpoint } : savedActiveTab,
        [savedActiveTab, listEndpoint, countEndpoint],
    )
    const [searchKeywords, setSearchKeywords] = useLocalStorage<string>(
        searchKeywordsStorageKey,
        "",
        searchKeywordsOverride,
    )
    const [filters, setFilters] = useState<URLSearchParams | null>(defaultFilters ?? null)

    return {
        activePhase,
        setActivePhase,
        activeTab,
        setActiveTab,
        activeTabOverride,
        searchKeywords,
        setSearchKeywords,
        filters,
        setFilters,
    }
}
